import { useState } from 'react';

import { BINARY_DECOMPOSITION } from '../../../../shared/featureFlags/flagNames';
import { FileType } from '../../../../shared/fileUpload';
import Locator from '../../../../shared/Locator';
import { isFeatureEnabled } from '../../../helpers/features';
import { nuc } from '../../../utils';
import { Icon, icons } from '../../generic';

import ModalUploadRevision from './ModalUploadRevision';

import styles from './DisplayProjectNav.module.less';

type Props = {
  locator: string;
};

/**
 * Get the appropriate API endpoint for downloading the original file
 */
const getDownloadEndpoint = (locator: string): string => {
  return `/api/revisions/${nuc(locator)}/original-sbom`;
};

/**
 * Button text component for the download button
 */
const DownloadButtonText = () => {
  return (
    <>
      <Icon icon={icons.down} />
      <span>Download Original</span>
    </>
  );
};

/**
 * Button text component for the upload button
 */
const UploadButtonText = ({ fileType }: { fileType: FileType.SBOM | FileType.Binary }) => {
  const fileTypeName = fileType === FileType.SBOM ? 'SBOM' : 'Binary';

  return (
    <>
      <Icon icon={icons.up} />
      <span>Upload New {fileTypeName}</span>
    </>
  );
};

/**
 * Component that provides UI affordances for downloading and uploading revisions
 */
const RevisionAffordances = ({ locator }: Props) => {
  const [shouldShowModal, setShouldShowModal] = useState(false);

  // Determine if we should show this component based on project type
  const { fetcher } = Locator.parse(locator);
  const isBinaryProject = fetcher === 'binary';
  const fileType = fetcher === 'sbom' ? FileType.SBOM : FileType.Binary;
  const isRelevantProject = fetcher === 'sbom' || (isBinaryProject && isFeatureEnabled(BINARY_DECOMPOSITION));

  // Only show for relevant project types
  if (!isRelevantProject) {
    return null;
  }

  return (
    <div className={`${styles.hybridProjectSBOMAffordances} nav-project-description`}>
      {!isBinaryProject && (
        <p className={`animated fadeInDown fast ${styles.sbomControl}`}>
          <a className={styles.hybridProjectSBOMDownload} href={getDownloadEndpoint(locator)} download>
            <DownloadButtonText />
          </a>
        </p>
      )}
      <p className={`animated fadeInDown fast ${styles.sbomControl}`}>
        <button
          type="button"
          className={styles.hybridProjectSBOMUploadButton}
          onClick={() => {
            setShouldShowModal(true);
          }}
        >
          <UploadButtonText fileType={fileType} />
        </button>
      </p>
      <ModalUploadRevision fileType={fileType} onHide={() => setShouldShowModal(false)} shouldShow={shouldShowModal} />
    </div>
  );
};

export default RevisionAffordances;
